import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Grid, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {connect, useSelector} from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useUniversalTracking } from '../../lib/tracking/universalTracking';
import { fetchContent } from '../../redux/actions/contentful';
import { colors } from '../../theme/theme';
import {AppState, CustomizingState, SalesType} from '../../types/redux';
import { ContentfulState } from '../../redux/reducers/contentful/ContentfulState';
import { DocumentBreakpointWrapper } from '../DocumentModal/DocumentBreakpointWrapper';
import DocumentModal from '../DocumentModal/DocumentModal';

const useStyles = makeStyles((theme: Theme) => ({
	root: {},
	placeholder: {
		[theme.breakpoints.up('sm')]: {
			height: 50,
		},
	},
	linkWrapper: {
		backgroundColor: colors.grayA05,
		[theme.breakpoints.up('sm')]: {
			position: 'fixed',
			left: 0,
			right: 0,
			bottom: 0,
		},
		padding: theme.spacing(2),
	},
	link: {
		color: colors.grayA50,
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
	},
}));

interface Props extends StateProps, DispatchProps {}

const InternalLegalFooter: React.FC<Props> = (props) => {
	const { contentful } = props;
	const { t } = useTranslation();
	const { trackEvent } = useUniversalTracking();
	const classes = useStyles();
	const imprintMobileDoc = contentful.imprint_mobile
		? documentToReactComponents(contentful.imprint_mobile)
		: null;
	const [showImprintModal, setShowImprintModal] = useState(false);
	const privacyPolicyMobileDoc = contentful.privacy_policy_mobile
		? documentToReactComponents(contentful.privacy_policy_mobile)
		: null;
	const privacyPolicyBank11MobileDoc = contentful.privacy_bank11_policy_mobile
		? documentToReactComponents(contentful.privacy_bank11_policy_mobile)
		: null;
	const privacyPolicyDesktopDoc = contentful.privacy_policy?.fields?.file?.url;
	const privacyBank11PolicyDesktopDoc = contentful.privacy_bank11_policy?.fields?.file?.url;
	const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);

	const handleClickImprint = (event: React.MouseEvent) => {
		event.preventDefault();
		trackEvent({
			category: 'footer-open-document',
			action: 'click-event',
			label: 'footer Impressum',
		});
		setShowImprintModal(true);
	};

	const handleClickPrivacyPolicy = (event: React.MouseEvent) => {
		event.preventDefault();
		trackEvent({
			category: 'footer-open-document',
			action: 'click-event',
			label: 'footer Datenschutz',
		});
		setShowPrivacyPolicyModal(true);
	};

	const customizing: CustomizingState = useSelector<AppState, CustomizingState>( (state) => state.customizing );
	let isBank11 = customizing?.name==="bank11";

	return (
		<div className={classes.root}>
			<div className={classes.placeholder} />
			<Grid container justifyContent="center" className={classes.linkWrapper}>
				<button onClick={handleClickImprint} className={classes.link}>
					{t('legalFooter.imprint')}
				</button>
				<button onClick={handleClickPrivacyPolicy} className={classes.link}>
					{t('legalFooter.privacy')}
				</button>
			</Grid>
			<DocumentModal
				showModal={showImprintModal}
				handleCloseFn={() => setShowImprintModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={imprintMobileDoc}
					title="Impressum"
				/>
			</DocumentModal>
			<DocumentModal
				showModal={showPrivacyPolicyModal}
				handleCloseFn={() => setShowPrivacyPolicyModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={isBank11 ? privacyPolicyBank11MobileDoc : privacyPolicyMobileDoc}
					desktopIframeSrc={isBank11 ? privacyBank11PolicyDesktopDoc : privacyPolicyDesktopDoc}
					title="Datenschutzerklärung"
				/>
			</DocumentModal>
		</div>
	);
};

interface StateProps {
	contentful: ContentfulState;
}

interface DispatchProps {
	fetchContent(): void;
}

const mapStateToProps = (state: AppState) => ({
	contentful: state.contentful,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	fetchContent: () => {
		dispatch(fetchContent());
	},
});

export const LegalFooter = connect(
	mapStateToProps,
	mapDispatchToProps
)(InternalLegalFooter);
